import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Loader2, Save, RotateCcw, LayoutGrid, ArrowUp, ArrowDown, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import client from "@/api/client";
import enJson from "@/i18n/locales/en.json";
import arJson from "@/i18n/locales/ar.json";

// -----------------------------------------------------------------------------
// Block structure — mirrors LandingSections.js + TrustStrip so admins can edit
// every visible string on the marketing pages. Each field carries the resource
// namespace ('landing' or 'trustStrip') and its key inside that namespace.
// -----------------------------------------------------------------------------
const BLOCKS = [
  {
    id: "hero",
    ns: "hero",
    titleKey: "adminLanding.blockHero",
    fields: [
      { key: "headline", labelKey: "admin.headline" },
      { key: "subhead", labelKey: "admin.subhead", textarea: true },
      { key: "cta_contact", labelKey: "admin.ctaContact" },
    ],
  },
  {
    id: "journey_hero",
    ns: "journey_hero",
    titleKey: "adminLanding.blockJourneyHero",
    fields: [
      { key: "eyebrow", labelKey: "admin.journeyEyebrow" },
      { key: "title", labelKey: "admin.journeyTitle" },
      { key: "subtitle", labelKey: "admin.journeySubtitle", textarea: true },
    ],
  },
  {
    id: "marquee",
    ns: null,
    titleKey: "adminLanding.blockMarquee",
    layoutOnly: true,
    fields: [],
  },
  {
    id: "trust_strip",
    ns: "trustStrip",
    titleKey: "adminLanding.blockTrustStrip",
    fields: [
      { key: "vision", labelKey: "adminLanding.trustVisionLabel" },
      { key: "visionValue", labelKey: "adminLanding.trustVisionValue" },
      { key: "companies", labelKey: "adminLanding.trustCompaniesLabel" },
      { key: "companiesValue", labelKey: "adminLanding.trustCompaniesValue" },
      { key: "experts", labelKey: "adminLanding.trustExpertsLabel" },
      { key: "expertsValue", labelKey: "adminLanding.trustExpertsValue" },
      { key: "sla", labelKey: "adminLanding.trustSlaLabel" },
      { key: "slaValue", labelKey: "adminLanding.trustSlaValue" },
    ],
  },
  {
    id: "stages",
    ns: "landing",
    titleKey: "adminLanding.blockStages",
    fields: [
      { key: "stagesEyebrow", labelKey: "adminLanding.eyebrow" },
      { key: "stagesTitle", labelKey: "adminLanding.title_field" },
      { key: "stagesSubtitle", labelKey: "adminLanding.subtitle", textarea: true },
      { key: "stage1Title", labelKey: "adminLanding.stage1Title" },
      { key: "stage1Desc", labelKey: "adminLanding.stage1Desc", textarea: true },
      { key: "stage2Title", labelKey: "adminLanding.stage2Title" },
      { key: "stage2Desc", labelKey: "adminLanding.stage2Desc", textarea: true },
      { key: "stage3Title", labelKey: "adminLanding.stage3Title" },
      { key: "stage3Desc", labelKey: "adminLanding.stage3Desc", textarea: true },
      { key: "stage4Title", labelKey: "adminLanding.stage4Title" },
      { key: "stage4Desc", labelKey: "adminLanding.stage4Desc", textarea: true },
    ],
  },
  {
    id: "services",
    ns: "landing",
    titleKey: "adminLanding.blockServices",
    fields: [
      { key: "servicesEyebrow", labelKey: "adminLanding.eyebrow" },
      { key: "servicesTitle", labelKey: "adminLanding.title_field" },
      { key: "servicesSubtitle", labelKey: "adminLanding.subtitle", textarea: true },
      { key: "svcMarketTitle", labelKey: "adminLanding.svcMarketTitle" },
      { key: "svcMarketDesc", labelKey: "adminLanding.svcMarketDesc", textarea: true },
      { key: "svcFinanceTitle", labelKey: "adminLanding.svcFinanceTitle" },
      { key: "svcFinanceDesc", labelKey: "adminLanding.svcFinanceDesc", textarea: true },
      { key: "svcLegalTitle", labelKey: "adminLanding.svcLegalTitle" },
      { key: "svcLegalDesc", labelKey: "adminLanding.svcLegalDesc", textarea: true },
      { key: "svcHRTitle", labelKey: "adminLanding.svcHRTitle" },
      { key: "svcHRDesc", labelKey: "adminLanding.svcHRDesc", textarea: true },
      { key: "svcProcurementTitle", labelKey: "adminLanding.svcProcurementTitle" },
      { key: "svcProcurementDesc", labelKey: "adminLanding.svcProcurementDesc", textarea: true },
      { key: "svcMarketingTitle", labelKey: "adminLanding.svcMarketingTitle" },
      { key: "svcMarketingDesc", labelKey: "adminLanding.svcMarketingDesc", textarea: true },
      { key: "svcAdvisoryTitle", labelKey: "adminLanding.svcAdvisoryTitle" },
      { key: "svcAdvisoryDesc", labelKey: "adminLanding.svcAdvisoryDesc", textarea: true },
      { key: "svcDataRoomTitle", labelKey: "adminLanding.svcDataRoomTitle" },
      { key: "svcDataRoomDesc", labelKey: "adminLanding.svcDataRoomDesc", textarea: true },
    ],
  },
  {
    id: "testimonials",
    ns: "landing",
    titleKey: "adminLanding.blockTestimonials",
    fields: [
      { key: "testimonialsEyebrow", labelKey: "adminLanding.eyebrow" },
      { key: "testimonialsTitle", labelKey: "adminLanding.title_field" },
      { key: "t1Quote", labelKey: "adminLanding.t1Quote", textarea: true },
      { key: "t1Name", labelKey: "adminLanding.t1Name" },
      { key: "t1Role", labelKey: "adminLanding.t1Role" },
      { key: "t2Quote", labelKey: "adminLanding.t2Quote", textarea: true },
      { key: "t2Name", labelKey: "adminLanding.t2Name" },
      { key: "t2Role", labelKey: "adminLanding.t2Role" },
      { key: "t3Quote", labelKey: "adminLanding.t3Quote", textarea: true },
      { key: "t3Name", labelKey: "adminLanding.t3Name" },
      { key: "t3Role", labelKey: "adminLanding.t3Role" },
    ],
  },
  {
    id: "final_cta",
    ns: "landing",
    titleKey: "adminLanding.blockFinalCta",
    fields: [
      { key: "finalCtaTitle", labelKey: "adminLanding.title_field" },
      { key: "finalCtaSubtitle", labelKey: "adminLanding.subtitle", textarea: true },
      { key: "finalCtaButton", labelKey: "adminLanding.primaryButton" },
      { key: "finalCtaSecondary", labelKey: "adminLanding.secondaryButton" },
    ],
  },
];

function nsPayload(ns, en, ar) {
  if (ns === "trustStrip") return { trust_strip_overrides: { en, ar } };
  if (ns === "hero") {
    const hero = {};
    for (const k of Object.keys(en || {})) hero[`${k}_en`] = en[k];
    for (const k of Object.keys(ar || {})) hero[`${k}_ar`] = ar[k];
    return { hero };
  }
  if (ns === "journey_hero") {
    const jh = {};
    for (const k of Object.keys(en || {})) jh[`${k}_en`] = en[k];
    for (const k of Object.keys(ar || {})) jh[`${k}_ar`] = ar[k];
    return { journey_hero: jh };
  }
  return { landing_overrides: { en, ar } };
}

const HERO_FIELD_DEFAULTS = {
  headline_en: "Enter, operate, and scale in Saudi Arabia.",
  headline_ar: "ادخل، شغّل، ووسّع أعمالك في المملكة العربية السعودية.",
  subhead_en: "One operating system for market entry, shared services, and advisory across the Kingdom and the wider MEA region — designed for founders, corporates, and investors moving into KSA.",
  subhead_ar: "نظام تشغيل واحد لدخول السوق والخدمات المشتركة والاستشارات في المملكة ومنطقة الشرق الأوسط وشمال أفريقيا — مصمم للمؤسسين والشركات والمستثمرين المتجهين إلى السعودية.",
  cta_contact_en: "Contact us",
  cta_contact_ar: "تواصل معنا",
};

const JOURNEY_HERO_FIELD_DEFAULTS = {
  eyebrow_en: "Start your journey",
  eyebrow_ar: "ابدأ رحلتك",
  title_en: "Tell us your business — we'll build the roadmap",
  title_ar: "أخبرنا عن مشروعك — وسنبني لك خارطة الطريق",
  subtitle_en: "Pick your business, preview the playbooks, sign up with just email.",
  subtitle_ar: "اختر نشاطك، عاين البرامج، وانضم بالبريد الإلكتروني فقط.",
};

function defaultFor(ns, key, lang) {
  if (ns === "hero") return HERO_FIELD_DEFAULTS[`${key}_${lang}`] || "";
  if (ns === "journey_hero") return JOURNEY_HERO_FIELD_DEFAULTS[`${key}_${lang}`] || "";
  const src = lang === "ar" ? arJson : enJson;
  return src?.[ns]?.[key] || "";
}

const DEFAULT_ORDER = ["journey_hero", "hero", "marquee", "trust_strip", "stages", "services", "testimonials", "final_cta"];

export function AdminLandingSectionsManager() {
  const { t, i18n } = useTranslation();
  const [landingOverrides, setLandingOverrides] = useState({ en: {}, ar: {} });
  const [trustOverrides, setTrustOverrides] = useState({ en: {}, ar: {} });
  const [heroOverrides, setHeroOverrides] = useState({ en: {}, ar: {} });
  const [journeyHeroOverrides, setJourneyHeroOverrides] = useState({ en: {}, ar: {} });
  const [layout, setLayout] = useState({
    order: DEFAULT_ORDER,
    visibility: {
      trust_strip: true,
      stages: true,
      services: true,
      testimonials: true,
      final_cta: true,
    },
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await client.get("/content");
      setLandingOverrides({
        en: { ...(res.data?.landing_overrides?.en || {}) },
        ar: { ...(res.data?.landing_overrides?.ar || {}) },
      });
      setTrustOverrides({
        en: { ...(res.data?.trust_strip_overrides?.en || {}) },
        ar: { ...(res.data?.trust_strip_overrides?.ar || {}) },
      });
      // Hero content lives at `content.hero.{key}_en/_ar` — split it back
      // into the {en,ar} shape the block editor uses internally.
      const hero = res.data?.hero || {};
      const enHero = {};
      const arHero = {};
      for (const k of Object.keys(hero)) {
        if (k.endsWith("_en")) enHero[k.slice(0, -3)] = hero[k];
        else if (k.endsWith("_ar")) arHero[k.slice(0, -3)] = hero[k];
      }
      setHeroOverrides({ en: enHero, ar: arHero });
      // journey_hero: same flat `{key}_en/_ar` shape as hero.
      const jh = res.data?.journey_hero || {};
      const enJh = {}, arJh = {};
      for (const k of Object.keys(jh)) {
        if (k.endsWith("_en")) enJh[k.slice(0, -3)] = jh[k];
        else if (k.endsWith("_ar")) arJh[k.slice(0, -3)] = jh[k];
      }
      setJourneyHeroOverrides({ en: enJh, ar: arJh });
      const remoteLayout = res.data?.landing_layout || {};
      const remoteOrder = Array.isArray(remoteLayout.order) && remoteLayout.order.length
        ? remoteLayout.order.filter((id) => DEFAULT_ORDER.includes(id))
        : DEFAULT_ORDER;
      // Append any known ids missing from the remote order.
      const merged = [...remoteOrder];
      for (const id of DEFAULT_ORDER) if (!merged.includes(id)) merged.push(id);
      setLayout({
        order: merged,
        visibility: {
          hero: true, journey_hero: true, marquee: true,
          trust_strip: true, stages: true, services: true, testimonials: true, final_cta: true,
          ...(remoteLayout.visibility || {}),
        },
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const overridesFor = useCallback(
    (ns) => {
      if (ns === "trustStrip") return trustOverrides;
      if (ns === "hero") return heroOverrides;
      if (ns === "journey_hero") return journeyHeroOverrides;
      return landingOverrides;
    },
    [trustOverrides, landingOverrides, heroOverrides, journeyHeroOverrides],
  );

  const setValue = (ns, lang, key, value) => {
    if (ns === "trustStrip") {
      setTrustOverrides((prev) => ({ ...prev, [lang]: { ...prev[lang], [key]: value } }));
    } else if (ns === "hero") {
      setHeroOverrides((prev) => ({ ...prev, [lang]: { ...prev[lang], [key]: value } }));
    } else if (ns === "journey_hero") {
      setJourneyHeroOverrides((prev) => ({ ...prev, [lang]: { ...prev[lang], [key]: value } }));
    } else {
      setLandingOverrides((prev) => ({ ...prev, [lang]: { ...prev[lang], [key]: value } }));
    }
  };

  const saveBlock = async (block) => {
    setSaving(block.id);
    try {
      // Build a minimal patch: only the keys in this block, per language.
      const enPatch = {};
      const arPatch = {};
      const current = overridesFor(block.ns);
      for (const f of block.fields) {
        if (current.en[f.key] !== undefined) enPatch[f.key] = current.en[f.key];
        if (current.ar[f.key] !== undefined) arPatch[f.key] = current.ar[f.key];
      }
      await client.put("/admin/content", nsPayload(block.ns, enPatch, arPatch));
      toast.success(t("admin.saved"));
      // Re-fetch to trigger context bridge -> live rerender across the site.
      window.dispatchEvent(new CustomEvent("site-content:refresh"));
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    } finally {
      setSaving(null);
    }
  };

  const resetBlock = async (block) => {
    setSaving(block.id);
    try {
      // Setting each field to empty string effectively removes the override
      // (empty values are pruned at the i18n bridge).
      const enPatch = {};
      const arPatch = {};
      for (const f of block.fields) {
        enPatch[f.key] = "";
        arPatch[f.key] = "";
      }
      await client.put("/admin/content", nsPayload(block.ns, enPatch, arPatch));
      toast.success(t("adminLanding.resetDone"));
      window.dispatchEvent(new CustomEvent("site-content:refresh"));
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    } finally {
      setSaving(null);
    }
  };

  const isAr = i18n.language === "ar";

  const renderField = (ns, f) => {
    const enVal = overridesFor(ns).en[f.key] ?? "";
    const arVal = overridesFor(ns).ar[f.key] ?? "";
    const enPh = defaultFor(ns, f.key, "en");
    const arPh = defaultFor(ns, f.key, "ar");
    const CommonProps = {
      "data-testid": `admin-landing-${ns}-${f.key}`,
    };
    return (
      <div className="space-y-1.5" key={`${ns}-${f.key}`}>
        <Label className="text-sm">{t(f.labelKey, { defaultValue: f.key })}</Label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {f.textarea ? (
            <textarea
              className="rounded-xl border border-input bg-transparent px-3 py-2 text-sm min-h-20 resize-y"
              value={enVal}
              placeholder={enPh}
              onChange={(e) => setValue(ns, "en", f.key, e.target.value)}
              data-testid={`${CommonProps["data-testid"]}-en`}
            />
          ) : (
            <Input
              value={enVal}
              placeholder={enPh}
              onChange={(e) => setValue(ns, "en", f.key, e.target.value)}
              className="rounded-xl"
              data-testid={`${CommonProps["data-testid"]}-en`}
            />
          )}
          {f.textarea ? (
            <textarea
              dir="rtl"
              className="rounded-xl border border-input bg-transparent px-3 py-2 text-sm min-h-20 resize-y"
              value={arVal}
              placeholder={arPh}
              onChange={(e) => setValue(ns, "ar", f.key, e.target.value)}
              data-testid={`${CommonProps["data-testid"]}-ar`}
            />
          ) : (
            <Input
              dir="rtl"
              value={arVal}
              placeholder={arPh}
              onChange={(e) => setValue(ns, "ar", f.key, e.target.value)}
              className="rounded-xl"
              data-testid={`${CommonProps["data-testid"]}-ar`}
            />
          )}
        </div>
      </div>
    );
  };

  const totalKeys = useMemo(
    () => BLOCKS.reduce((n, b) => n + b.fields.length, 0),
    [],
  );

  // Save the current layout (order + visibility) to the backend and let the
  // context bridge re-render the public site.
  const persistLayout = async (nextLayout) => {
    try {
      await client.put("/admin/content", { landing_layout: nextLayout });
      window.dispatchEvent(new CustomEvent("site-content:refresh"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
      // Revert local UI on failure so admin sees actual state.
      await load();
    }
  };

  const moveBlock = (blockId, delta) => {
    const cur = layout.order.indexOf(blockId);
    if (cur < 0) return;
    const next = cur + delta;
    if (next < 0 || next >= layout.order.length) return;
    const newOrder = [...layout.order];
    [newOrder[cur], newOrder[next]] = [newOrder[next], newOrder[cur]];
    const nextLayout = { ...layout, order: newOrder };
    setLayout(nextLayout);
    persistLayout(nextLayout);
    toast.success(t("adminLanding.reordered"));
  };

  const toggleVisibility = (blockId, on) => {
    const nextLayout = {
      ...layout,
      visibility: { ...layout.visibility, [blockId]: on },
    };
    setLayout(nextLayout);
    persistLayout(nextLayout);
  };

  // Render blocks in the admin panel following the same order the public page
  // uses so the editor mirrors the visitor experience.
  const orderedBlocks = useMemo(
    () => layout.order.map((id) => BLOCKS.find((b) => b.id === id)).filter(Boolean),
    [layout.order],
  );

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-panel-landing-sections">
      <Card className="rounded-2xl border border-border p-6" data-testid="admin-landing-intro">
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <LayoutGrid className="h-5 w-5" />
          </span>
          <div className="min-w-0">
            <h2 className="font-display text-xl font-semibold">{t("adminLanding.title")}</h2>
            <p className="text-sm text-muted-foreground mt-1">
              {t("adminLanding.description", { count: totalKeys })}
            </p>
            <p className="text-xs text-muted-foreground mt-2">
              {isAr ? "الحقول الفارغة تعود إلى النص الافتراضي." : "Leaving a field empty keeps the default copy."}
            </p>
          </div>
        </div>
      </Card>

      {orderedBlocks.map((block, idx) => {
        const visible = layout.visibility[block.id] !== false;
        const isFirst = idx === 0;
        const isLast = idx === orderedBlocks.length - 1;
        return (
        <Card
          key={block.id}
          className="rounded-2xl border border-border p-6 space-y-5"
          data-testid={`admin-landing-block-${block.id}`}
        >
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="flex flex-col gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 rounded-lg text-muted-foreground"
                  onClick={() => moveBlock(block.id, -1)}
                  disabled={isFirst || saving === block.id}
                  aria-label={t("adminLanding.moveUp")}
                  data-testid={`admin-landing-move-up-${block.id}`}
                >
                  <ArrowUp className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 rounded-lg text-muted-foreground"
                  onClick={() => moveBlock(block.id, +1)}
                  disabled={isLast || saving === block.id}
                  aria-label={t("adminLanding.moveDown")}
                  data-testid={`admin-landing-move-down-${block.id}`}
                >
                  <ArrowDown className="h-4 w-4" />
                </Button>
              </div>
              <h3 className="font-display text-lg font-semibold truncate">{t(block.titleKey)}</h3>
              {!visible && (
                <span
                  className="inline-flex items-center gap-1 rounded-full border border-border px-2 py-0.5 text-[10px] uppercase tracking-widest text-muted-foreground"
                  data-testid={`admin-landing-hidden-badge-${block.id}`}
                >
                  <EyeOff className="h-3 w-3" /> {t("adminLanding.hidden")}
                </span>
              )}
            </div>
          <div className="flex items-center gap-3 flex-wrap">
            {!block.layoutOnly && (
              <div className="flex items-center gap-2">
                {visible ? <Eye className="h-4 w-4 text-primary" /> : <EyeOff className="h-4 w-4 text-muted-foreground" />}
                <Switch
                  checked={visible}
                  onCheckedChange={(v) => toggleVisibility(block.id, v)}
                  aria-label={t("adminLanding.visibilityAria")}
                  data-testid={`admin-landing-visibility-${block.id}`}
                />
              </div>
            )}
            {block.layoutOnly && (
              <div className="flex items-center gap-2">
                {visible ? <Eye className="h-4 w-4 text-primary" /> : <EyeOff className="h-4 w-4 text-muted-foreground" />}
                <Switch
                  checked={visible}
                  onCheckedChange={(v) => toggleVisibility(block.id, v)}
                  aria-label={t("adminLanding.visibilityAria")}
                  data-testid={`admin-landing-visibility-${block.id}`}
                />
              </div>
            )}
            {!block.layoutOnly && (
              <Button
                variant="outline"
                size="sm"
                className="rounded-xl gap-1.5"
                onClick={() => resetBlock(block)}
                disabled={saving === block.id}
                data-testid={`admin-landing-reset-${block.id}`}
              >
                <RotateCcw className="h-3.5 w-3.5" /> {t("adminLanding.reset")}
              </Button>
            )}
            {!block.layoutOnly && (
              <Button
                className="btn-brand rounded-xl gap-1.5"
                size="sm"
                onClick={() => saveBlock(block)}
                disabled={saving === block.id}
                data-testid={`admin-landing-save-${block.id}`}
              >
                {saving === block.id ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                {t("common.save")}
              </Button>
            )}
          </div>
          </div>
          <Separator />
          {block.layoutOnly ? (
            <p className="text-xs text-muted-foreground">
              {isAr ? "استخدم أزرار الترتيب والإظهار للتحكم في هذا القسم — يحرَّر محتواه من مكانه المخصص." : "Use the reorder and visibility controls to place this section — its content is edited in its own panel."}
            </p>
          ) : (
            <div className="space-y-4">
              {block.fields.map((f) => renderField(block.ns, f))}
            </div>
          )}
        </Card>
        );
      })}
    </div>
  );
}

export default AdminLandingSectionsManager;
